// src/utils/authHelpers.js
// Auth.js'den ayrıştırılan yardımcı fonksiyonlar ve sabitler
// Faz-4 Adım 3

/* ─────────────────────────────────────────────────────────────
   SABİTLER
───────────────────────────────────────────────────────────── */

// EMİR #96.3 — TEK KAYNAK. Buradaki satır-içi liste router'ın rezerve
// rotalarını (place/sounds/clips/map/card/auth...) İÇERMİYORDU → o adlar
// alınabiliyor ve alan kullanıcı kendi profiline giremiyordu.
// ⚠️ `export { X as Y } from` YEREL BAĞ OLUŞTURMAZ — modül içinde
// kullanılan bir ismi öyle bağlarsan "is not defined" alırsın (mevcut
// test seti bunu anında yakaladı). import + export const şart.
import { TUM_REZERVE } from "../constants/reservedUsernames";

export const INTEREST_OPTIONS = [
  'Müzik', 'Spor', 'Seyahat', 'Yemek', 'Teknoloji',
  'Sanat', 'Film', 'Kitap', 'Oyun', 'Moda',
  'Fotoğrafçılık', 'Dans', 'Doğa', 'Fitness', 'Yoga',
];

// Instagram-parite sadeleştirme (2026-06-30): fotoğraf/ilgi-alanı/takip-önerisi
// adımları kayıttan çıkarıldı — zaten kayıt-sonrası WelcomeWizard (interests+
// follows+push) bunları atlanabilir şekilde karşılıyor (hooks/useWelcomeWizard.ts,
// components/WelcomeWizard.tsx). Kayıt artık: temel bilgiler → doğum tarihi.
export const TOTAL_STEPS = 2;

export const RESERVED_USERNAMES = TUM_REZERVE;

/* ─────────────────────────────────────────────────────────────
   YARDIMCI FONKSİYONLAR
───────────────────────────────────────────────────────────── */

/**
 * Kullanıcı adı format doğrulama
 * Kural: 3-30 karakter, sadece a-z, 0-9, nokta, alt çizgi
 */
export function validateUsername(username?: string | null): string | null {
  if (!username || username.length < 3) return 'Kullanıcı adı en az 3 karakter olmalıdır.';
  if (username.length > 30) return 'Kullanıcı adı en fazla 30 karakter olabilir.';
  if (!/^[a-z0-9._]+$/.test(username)) return 'Sadece küçük harf, rakam, nokta (.) ve alt çizgi (_) kullanabilirsiniz.';
  if (/^[._]|[._]$/.test(username)) return 'Kullanıcı adı nokta veya alt çizgi ile başlayıp bitemez.';
  if (/[._]{2,}/.test(username)) return 'Ardışık nokta veya alt çizgi kullanılamaz.';
  if (RESERVED_USERNAMES.has(username)) return 'Bu kullanıcı adı rezerve edilmiştir.';
  return null;
}

/**
 * E-posta format doğrulama
 */
export function validateEmail(email?: string | null): string | null {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  if (!email || !re.test(email.trim())) return 'Geçerli bir e-posta adresi girin.';
  return null;
}

/**
 * Şifre güç doğrulama
 */
export function validatePassword(password?: string | null): string | null {
  if (!password || password.length < 6) return 'Şifre en az 6 karakter olmalıdır.';
  if (password.length > 128) return 'Şifre çok uzun.';
  return null;
}

/**
 * 13 yaş kapısı — İSTEMCİ tarafı (tek kopya).
 *
 * ⚠️ BU BİR GÜVENLİK KAPISI DEĞİL, bir KULLANICI DENEYİMİ kapısıdır: kararı
 * veren merci sunucudur (`functions/src/shared/yasKapisi.ts`). Buradaki
 * kontrol yalnız kullanıcıyı boş yere sunucuya göndermemek içindir ve
 * by-pass edilebilir olduğu VARSAYILIR.
 *
 * ⚠️ Sunucu artık FAIL-CLOSED: doğum tarihi eksik/biçimsizse kayıt REDDEDİLİR.
 * Bu yüzden istemcinin bu alanı BOŞ göndermesi bir seçenek değil — sosyal
 * kayıt tam olarak bunu yapıyordu (alan hiç toplanmıyordu).
 *
 * Kurallar sunucudakiyle AYNI: eksik/biçimsiz/takvim-dışı/gelecek/<13/>120.
 */
export function validateBirthday(birthday?: string | null): string | null {
  const s = String(birthday ?? '').trim();
  if (!s) return 'Lütfen doğum tarihinizi girin.';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return 'Lütfen geçerli bir doğum tarihi girin.';
  const yil = Number(m[1]);
  const ay = Number(m[2]);
  const gun = Number(m[3]);
  // `new Date("2001-02-30")` sessizce taşar → bileşen karşılaştırmasıyla ele.
  const d = new Date(Date.UTC(yil, ay - 1, gun));
  if (
    d.getUTCFullYear() !== yil ||
    d.getUTCMonth() !== ay - 1 ||
    d.getUTCDate() !== gun
  ) {
    return 'Lütfen geçerli bir doğum tarihi girin.';
  }
  const simdi = new Date();
  if (d.getTime() > simdi.getTime()) return 'Lütfen geçerli bir doğum tarihi girin.';
  let yas = simdi.getUTCFullYear() - yil;
  const ayFark = simdi.getUTCMonth() - (ay - 1);
  if (ayFark < 0 || (ayFark === 0 && simdi.getUTCDate() < gun)) yas--;
  if (yas < 13) return 'Mylasa 13 yaş altı kullanıcıları kabul etmemektedir.';
  if (yas > 120) return 'Lütfen geçerli bir doğum tarihi girin.';
  return null;
}

/**
 * Firebase Auth hata kodlarını Türkçe mesaja çevir
 */
export function authErrorMessage(err?: unknown): string {
  // GEREKÇELİ `as`: çağıranlar (Auth.tsx) `catch` değişkenini doğrudan
  // geçiriyor; strict altında o değişken `unknown`. İmzayı `{code?:string}`e
  // daraltmak borcu çağıranlara taşırdı. `as` yalnız `code` okumak için —
  // derlenen JS'e yansımaz, davranış aynı.
  const code = (err as { code?: string } | null | undefined)?.code || '';
  const messages: Record<string, string> = {
    'auth/email-already-in-use':     'Bu e-posta adresi zaten kullanılıyor.',
    'auth/invalid-email':            'Geçersiz e-posta adresi.',
    'auth/weak-password':            'Şifre çok zayıf. En az 6 karakter kullanın.',
    'auth/wrong-password':           'E-posta veya şifre hatalı.',
    'auth/user-not-found':           'Bu e-posta ile kayıtlı hesap bulunamadı.',
    'auth/invalid-credential':       'E-posta veya şifre hatalı.',
    'auth/user-disabled':            'Bu hesap devre dışı bırakılmıştır. Destek için iletişime geçin.',
    'auth/too-many-requests':        'Çok fazla başarısız deneme. Lütfen birkaç dakika bekleyin.',
    'auth/network-request-failed':   'İnternet bağlantınızı kontrol edin.',
    'auth/operation-not-allowed':    'Bu giriş yöntemi şu an kullanılamıyor.',
    'auth/requires-recent-login':    'Bu işlem için yeniden giriş yapmanız gerekiyor.',
    'auth/account-exists-with-different-credential': 'Bu e-posta farklı bir yöntemle kayıtlı.',
    'auth/popup-closed-by-user':     'Giriş penceresi kapatıldı.',
    'auth/cancelled-popup-request':  'Giriş isteği iptal edildi.',
    'auth/popup-blocked':            'Tarayıcı açılır pencereyi engelledi.',
    'auth/timeout':                  'İstek zaman aşımına uğradı. Tekrar deneyin.',
    'auth/quota-exceeded':           'Geçici olarak çok fazla istek yapıldı. Lütfen bekleyin.',
  };
  return messages[code] || `Bir hata oluştu. Lütfen tekrar deneyin. (${code || 'bilinmeyen'})`;
}

/**
 * Firestore'a yazma — 3 kez retry, exponential backoff
 */
export async function writeWithRetry<T>(fn: () => T, maxRetries = 3) {
  let lastErr;
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (i < maxRetries - 1) {
        await new Promise(r => setTimeout(r, 500 * Math.pow(2, i)));
      }
    }
  }
  throw lastErr;
}
